import { createVendorInvite, type VendorInviteIssuedView } from '@api/vendorInvite';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    CopyButton,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useState, type FC } from 'react';

interface InviteLoginModalProps {
    opened: boolean;
    onClose: () => void;
    /** The site whose page the button lives on. Never picked here - that is the point. */
    warehouseId: string;
    siteName: string;
}

/**
 * Invite somebody to run this site's portal.
 *
 * THE SITE IS NOT AN INPUT. The modal opens from one site's page and invites to that site,
 * which is what pins the resulting login's scope - by the time this renders, the one decision
 * that matters is already made by where the brand is standing.
 *
 * THE LINK STAYS ON SCREEN AFTER SENDING, prominent when the mail failed and quiet when it
 * went, because email is the least reliable part of this chain and a shop floor that never
 * got the message needs the brand to hand the link over some other way.
 */
const InviteLoginModal: FC<InviteLoginModalProps> = ({
    opened,
    onClose,
    warehouseId,
    siteName,
}) => {
    const { showToastError } = useToaster();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [issued, setIssued] = useState<VendorInviteIssuedView | null>(null);

    const reset = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setIssued(null);
        setIsSending(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSend = async () => {
        setIsSending(true);
        try {
            setIssued(
                await createVendorInvite({
                    email: email.trim(),
                    warehouseId,
                    firstName: firstName.trim() || undefined,
                    lastName: lastName.trim() || undefined,
                }),
            );
        } catch (err: any) {
            showToastError({
                title: 'Could not send the invite',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={`Invite a login for ${siteName}`}
            size="md"
            centered
        >
            {issued ? (
                <Stack gap="md">
                    <Text size="sm">
                        {issued.emailed
                            ? `${issued.reused ? 'Re-sent the invite' : 'Invite sent'} to ${issued.email}. `
                              + 'They have 7 days to set up their login.'
                            : `The invite for ${issued.email} was created, but the email could `
                              + 'not be sent. Share this link with them yourself:'}
                    </Text>

                    {/* Kept visible even when mailed - "it never arrived" is a support call
                        this box answers without another round trip. */}
                    <Group gap={8} wrap="nowrap">
                        <TextInput value={issued.link} readOnly style={{ flex: 1 }} />
                        <CopyButton value={issued.link}>
                            {({ copied, copy }) => (
                                <Button
                                    variant={ButtonVariant.DEFAULT}
                                    onClick={copy}
                                >
                                    {copied ? 'Copied' : 'Copy'}
                                </Button>
                            )}
                        </CopyButton>
                    </Group>

                    <Group justify="flex-end">
                        <Button onClick={handleClose}>Done</Button>
                    </Group>
                </Stack>
            ) : (
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        They&apos;ll get a link to choose a password. The login only sees this
                        site&apos;s orders and stock.
                    </Text>

                    <TextInput
                        label="Email"
                        placeholder="floor@manufacturer.example"
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        withAsterisk
                    />

                    <Group grow>
                        <TextInput
                            label="First name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.currentTarget.value)}
                        />
                        <TextInput
                            label="Last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.currentTarget.value)}
                        />
                    </Group>

                    <Group justify="flex-end" gap={8}>
                        <Button variant={ButtonVariant.DEFAULT} onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSend}
                            loading={isSending}
                            disabled={!/^\S+@\S+\.\S+$/.test(email.trim())}
                        >
                            Send invite
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};

export default InviteLoginModal;
